// packages/shared/src/ledger/enums.ts
//
// Ledger domain enums + Zod schemas — canonical source of truth for all
// ledger-domain string unions across services, routes, and downstream sub-phases
// (3.2-3.5).  Definitions keyed to:
//   AccountType → (11 account types, authoritative over's 9)
//   TxGroupType → TX group tags (grown per phase — see the TxGroupTypeSchema
//                 block below for the current count and per-phase history)
//   PostingType → (12 posting types per journal design)
//   BalanceSide → balance computation convention
//
// Node-pure (no Fastify / Mongoose imports). Safe to bundle in Vite client builds.
import { z } from "zod";
// ---------------------------------------------------------------------------
// AccountType — 11 variants
// ---------------------------------------------------------------------------
export const AccountTypeSchema = z.enum([
    // Per-merchant (1-1)
    "merchant_wallet", // normal CR  — primary spending balance
    "merchant_credit_liability", // normal CR  — credit facility outstanding
    "merchant_invoice_receivable", // normal DR  — owed TO merchant (Amendment #6)
    "credit_memo_liability", // normal CR  — issued credit memos (Amendment #7)
    "spending_held", // normal CR  — pre-authorized hold pool
    "spending_consumed", // normal DR  — post-capture consumed pool
    // Global
    "platform_cash", // normal DR  — platform treasury
    "platform_revenue", // normal CR  — earned revenue
    "fx_gain_loss", // signed     — DR=realized FX loss, CR=gain
    // Per-jurisdiction (1-N per Amendment #7)
    "tax_payable",
    // Per-case (1-N, e.g. chargeback)
    "outstanding_debt", // normal DR  — merchant owes platform
]);
// ---------------------------------------------------------------------------
// TxGroupType — 23 variants
// Phase 3 baseline: 13 variants
//   (PLAN text "12" was off-by-one; both TRANSFER_SENT and TRANSFER_RECEIVED
//    are distinct entries; CHARGEBACK_RECOVERY is the 13th)
// Phase 3.4b amendment #1: +1 CREDIT_REFUND (refund of credit-paid order, P3.D-21a)
// Phase 4.1 extension: +9 invoice/credit-memo lifecycle variants per
//    (final shape) and (daily reconciliation
//   cron in Phase 11 must continue to find a paired ledger entry for EVERY
//   tx_group type — adding values is backward-compatible because callers
//   parse via TxGroupTypeSchema.parse() at runtime).
// ---------------------------------------------------------------------------
export const TxGroupTypeSchema = z.enum([
    // ── Phase 3 baseline (13) ──────────────────────────────────────────────
    "TOP_UP", // merchant manual top-up via payment gateway
    "INSTANT_TOPUP", // auto-recharge also uses this (metadata.trigger="auto_recharge")
    "ORDER_CHARGE", // debit wallet on order placement
    "SURCHARGE_CHARGE", // surcharge line-item charge
    "EMBROIDERY_CHARGE", // embroidery add-on charge
    "ORDER_REFUND", // credit back on order cancellation / refund
    "TRANSFER_SENT", // merchant-to-merchant transfer: sender leg
    "TRANSFER_RECEIVED", // merchant-to-merchant transfer: receiver leg (paired)
    "WITHDRAW", // merchant withdrawal request
    "CREDIT_USAGE", // credit facility draw-down
    "CREDIT_PAYMENT", // merchant repays credit facility
    "CREDIT_REFUND", // refund of a credit-paid order — reverses CREDIT_USAGE (P3.D-21a)
    "CASHBACK", // platform-issued cashback reward
    "CHARGEBACK_RECOVERY", // disputed charge recovered from merchant outstanding_debt
    // ── Phase 4.1 invoice / credit-memo lifecycle (+9) ─────────────────────
    "INVOICE_CHARGE", // invoice issued — DR receivable / CR revenue
    "INVOICE_TAX_CHARGE", // invoice tax leg — DR receivable / CR tax_payable
    "INVOICE_PAYMENT", // merchant pays invoice — DR cash / CR receivable
    "INVOICE_PAYMENT_REVERSAL", // payment reversed (bounce/dispute)
    "CREDIT_MEMO_ISSUED", // credit memo issued — DR revenue / CR credit_memo_liability
    "CREDIT_MEMO_APPLIED", // applied against open invoice — DR liability / CR receivable
    "CREDIT_MEMO_TAX_ADJUSTMENT", // tax adjustment leg of a credit memo
    "CREDIT_MEMO_CONVERT", // convert credit memo balance → wallet balance
    "CREDIT_MEMO_REFUND_BANK", // refund credit memo balance to merchant bank account
]);
// ---------------------------------------------------------------------------
// PostingType — 12 variants journal design
// ---------------------------------------------------------------------------
export const PostingTypeSchema = z.enum([
    "topup", // wallet top-up credit
    "hold", // pre-authorization hold
    "capture", // convert hold → consumed on order
    "release", // release un-captured hold
    "refund", // credit back captured amount
    "withdraw", // withdrawal debit
    "adjustment", // manual balance correction
    "fx_gain_loss", // realized FX gain/loss entry
    "chargeback", // chargeback debit (disputed charge)
    "chargeback_resolve", // resolution of previously posted chargeback
    "commission", // platform commission credit/debit
    "rounding", // cent-rounding correction
]);
// ---------------------------------------------------------------------------
// LedgerDirection — double-entry DR / CR side
// ---------------------------------------------------------------------------
export const LedgerDirectionSchema = z.enum(["DR", "CR"]);
// ---------------------------------------------------------------------------
// AccountStatus
// ---------------------------------------------------------------------------
export const AccountStatusSchema = z.enum(["active", "frozen", "closed"]);
// ---------------------------------------------------------------------------
// BalanceSide lookup — balance computation convention
//
// "CR"     → normal-credit account: balance = Σ(CR) - Σ(DR)   (liabilities, equity)
// "DR"     → normal-debit  account: balance = Σ(DR) - Σ(CR)   (assets, receivables)
// "signed" → P&L absorber (fx_gain_loss): balance = Σ(CR) - Σ(DR) and may be neg
// ---------------------------------------------------------------------------
export const BalanceSide = {
    merchant_wallet: "CR",
    merchant_credit_liability: "CR",
    merchant_invoice_receivable: "DR",
    credit_memo_liability: "CR",
    platform_cash: "DR",
    platform_revenue: "CR",
    fx_gain_loss: "signed",
    tax_payable: "CR",
    outstanding_debt: "DR",
    spending_held: "CR",
    spending_consumed: "DR",
};
//# sourceMappingURL=enums.js.map